import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ExtractionSession {
  id: string;
  status: string;
  progress: number;
  extractedData: Record<string, any>;
  confidenceScores: Record<string, number>;
  missingFields: string[];
  processingTimeMs?: number;
  aiProvider?: string;
}

interface FieldSearchResult {
  fieldName: string;
  value: string | null;
  confidence: number;
  reasoning: string;
  suggestions: string[];
  updated: boolean;
}

export function useHybridExtraction() {
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [session, setSession] = useState<ExtractionSession | null>(null);
  const [searchResults, setSearchResults] = useState<Record<string, FieldSearchResult>>({});
  const { toast } = useToast();

  const startExtraction = useCallback(async (
    extractedText: string,
    options: {
      queueItemId?: string;
      billId?: string;
      sessionType?: string;
      existingData?: Record<string, any>;
    } = {}
  ): Promise<ExtractionSession | null> => {
    setIsExtracting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('hybrid-bill-extraction', {
        body: {
          extractedText,
          queueItemId: options.queueItemId,
          billId: options.billId,
          sessionType: options.sessionType || 'full_extraction',
          existingData: options.existingData
        }
      });

      if (error) throw error;

      const extractionSession: ExtractionSession = {
        id: data.sessionId,
        status: data.status,
        progress: data.progress,
        extractedData: data.extractedData,
        confidenceScores: data.confidenceScores,
        missingFields: data.missingFields,
        processingTimeMs: data.processingTime,
        aiProvider: 'openai'
      };

      setSession(extractionSession);

      if (data.missingFields?.length > 0) {
        toast({
          title: "Extraction Complete",
          description: `Found ${Object.keys(data.extractedData).length} fields. ${data.missingFields.length} fields need attention.`,
        });
      } else {
        toast({
          title: "Extraction Complete",
          description: "All fields extracted successfully!",
        });
      }

      return extractionSession;
    } catch (error) {
      console.error('Extraction error:', error);
      toast({
        title: "Extraction Failed",
        description: error.message || "An error occurred during extraction",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsExtracting(false);
    }
  }, [toast]);

  const searchField = useCallback(async (
    fieldName: string,
    extractedText: string,
    options: {
      context?: Record<string, any>;
      previousAttempts?: string[];
    } = {}
  ): Promise<FieldSearchResult | null> => {
    if (!session) {
      toast({
        title: "No Session",
        description: "Please start an extraction session first",
        variant: "destructive",
      });
      return null;
    }

    setIsSearching(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-field-search', {
        body: {
          sessionId: session.id,
          fieldName,
          extractedText,
          context: options.context,
          previousAttempts: options.previousAttempts
        }
      });

      if (error) throw error;

      const result: FieldSearchResult = data;
      
      // Update search results state
      setSearchResults(prev => ({
        ...prev,
        [fieldName]: result
      }));

      // If the field was updated, refresh the session
      if (result.updated) {
        setSession(prev => {
          if (!prev) return prev;
          
          const updatedData = { ...prev.extractedData };
          const updatedScores = { ...prev.confidenceScores };
          const updatedMissingFields = [...prev.missingFields];
          
          if (result.value) {
            updatedData[fieldName] = result.value;
            updatedScores[fieldName] = result.confidence;
            
            // Remove from missing fields
            const fieldIndex = updatedMissingFields.indexOf(fieldName);
            if (fieldIndex > -1) {
              updatedMissingFields.splice(fieldIndex, 1);
            }
          }
          
          return {
            ...prev,
            extractedData: updatedData,
            confidenceScores: updatedScores,
            missingFields: updatedMissingFields
          };
        });

        toast({
          title: "Field Found",
          description: `${fieldName} has been updated with confidence ${Math.round(result.confidence * 100)}%`,
        });
      } else {
        toast({
          title: "Field Search Complete",
          description: result.reasoning,
        });
      }

      return result;
    } catch (error) {
      console.error('Field search error:', error);
      toast({
        title: "Search Failed",
        description: error.message || "An error occurred during field search",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsSearching(false);
    }
  }, [session, toast]);

  const getFieldConfidence = useCallback((fieldName: string): number => {
    return session?.confidenceScores[fieldName] || 0;
  }, [session]);

  const getFieldValue = useCallback((fieldName: string): any => {
    return session?.extractedData[fieldName] || null;
  }, [session]);

  const isMissingField = useCallback((fieldName: string): boolean => {
    return session?.missingFields.includes(fieldName) || false;
  }, [session]);

  const updateFieldValue = useCallback((fieldName: string, value: any) => {
    setSession(prev => {
      if (!prev) return prev;
      
      const updatedData = { ...prev.extractedData };
      const updatedMissingFields = [...prev.missingFields];
      
      updatedData[fieldName] = value;
      
      // Remove from missing fields if it was there
      const fieldIndex = updatedMissingFields.indexOf(fieldName);
      if (fieldIndex > -1) {
        updatedMissingFields.splice(fieldIndex, 1);
      }
      
      return {
        ...prev,
        extractedData: updatedData,
        missingFields: updatedMissingFields
      };
    });
  }, []);

  const getExtractionProgress = useCallback(() => {
    if (!session) return 0;
    
    const totalFields = Object.keys(session.extractedData).length + session.missingFields.length;
    const extractedFields = Object.keys(session.extractedData).length;
    
    return totalFields > 0 ? Math.round((extractedFields / totalFields) * 100) : 0;
  }, [session]);

  return {
    // State
    isExtracting,
    isSearching,
    session,
    searchResults,
    
    // Actions
    startExtraction,
    searchField,
    updateFieldValue,
    
    // Getters
    getFieldConfidence,
    getFieldValue,
    isMissingField,
    getExtractionProgress,
  };
}